import { ofType, Epic } from 'redux-observable'
import { of } from 'rxjs'
import { map, filter, mergeMap, pluck, switchMap, take, mergeAll } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isEqual } from 'lodash-es'
import { AnyAction } from 'redux'
import { chatbotEvents } from './state'
import * as chatbotSelectors from './selectors'
import { uiEvents } from '../state'
import { userApiSelectors, userApiEvents } from '../../api/user'
import { portfolioApiEvents, portfolioApiSelectors } from '../../api/portfolio'
import { propertyApiSelectors, propertyApiEvents } from '../../api/property'
import { propertyDetailsEvents, initialState as propertyDetailsInitialState } from './Dialogs/propertyDetailsSlice'
import { ownerDetailsEvents, initialState as ownerDetailsInitialState } from './Dialogs/ownerDetailsSlice'
import { tenantDetailsEvents, initialState as tenantDetailsInitialState } from './Dialogs/tenantDetailsSlice'
import { leaseTermEvents, initialState as leaseTermInitialState } from './Dialogs/leaseTermSlice'
import { leaseRentAndFeesEvents, initialState as leaseRentAndFeesInitialState } from './Dialogs/leaseRentAndFeesSlice'
import { leaseSettingsEvents, initialState as leaseSettingsInitialState } from './Dialogs/leaseSettingsSlice'
import { leaseCommissionEvents, initialState as leaseCommissionInitialState } from './Dialogs/leaseCommissionSlice'
import { leaseInvoicesEvents, initialState as leaseInvoicesInitialState } from './Dialogs/leaseInvoicesSlice'
import {
  leaseConfirmationEvents,
  initialState as leaseConfirmationInitialState,
} from './Dialogs/leaseConfirmationSlice'
import { userDetailsEvents, initialState as userDetailsInitialState } from './Dialogs/userDetailsSlice'
import { agencyDetailsEvents, initialState as agencyDetailsInitialState } from './Dialogs/agencyDetailsSlice'
import {
  getCurrentLeaseType,
  isPropertySelected,
  getSelectedProperty,
  isPlaceSelected,
  getSelectedPlace,
  isLightstonePropertySelected,
  getSelectedLightstoneProperty,
} from './selectors'
import { $TSFixMe } from 'types/ts-migrate'
import { agencyApiSelectors } from 'modules/api/agency'
import { layoutEvents } from 'modules/Layout/state'
import { EpicDependencies, RootState } from 'src/state/types'

export const injectPortfolioIdRedirectUrl: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(chatbotEvents.portfolioRedirect),
    map(({ payload }) => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return payload.replace(':id', id || 'new')
    }),
    map(uiEvents.redirect),
    tag('chatbot/epics/injectPortfolioIdRedirectUrl'),
  )

export const takeMeHome: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.takeMeHome),
    mergeMap(() =>
      of(chatbotEvents.resetCurrentPortfolio(), uiEvents.redirect('/dashboard'), layoutEvents.openSidebar('global')),
    ),
    tag('chatbot/epics/takeMeHome'),
  )

// Dialog initial states for change comparison — single source of truth is each slice's initialState.
const dialogInitialStates: Record<string, any> = {
  propertyDetails: propertyDetailsInitialState,
  ownerDetails: ownerDetailsInitialState,
  tenantDetails: tenantDetailsInitialState,
  leaseTerm: leaseTermInitialState,
  leaseRentAndFees: leaseRentAndFeesInitialState,
  leaseSettings: leaseSettingsInitialState,
  leaseCommission: leaseCommissionInitialState,
  leaseInvoices: leaseInvoicesInitialState,
  leaseConfirmation: leaseConfirmationInitialState,
  userDetails: userDetailsInitialState,
  agencyDetails: agencyDetailsInitialState,
}

// Map RTK action types to dialog names for change detection
const dialogActionTypeMap: Record<string, string[]> = {
  propertyDetails: [
    propertyDetailsEvents.googlePlaceSelected.type,
    propertyDetailsEvents.propertySelected.type,
    propertyDetailsEvents.lightstonePropertySelected.type,
    propertyDetailsEvents.clearSelection.type,
    portfolioApiEvents.amendProperty_success.type,
    portfolioApiEvents.amendMetaData_success.type,
  ],
  ownerDetails: [ownerDetailsEvents.partySelected.type, ownerDetailsEvents.partySaved.type],
  tenantDetails: [tenantDetailsEvents.partySelected.type, tenantDetailsEvents.partySaved.type],
  leaseTerm: [leaseTermEvents.selectLeaseTerms.type, portfolioApiEvents.amendTerms_success.type],
  leaseRentAndFees: [leaseRentAndFeesEvents.rentAndFeesSaved.type, portfolioApiEvents.amendRentAndFees_success.type],
  leaseSettings: [leaseSettingsEvents.settingsSaved.type, portfolioApiEvents.amendSettings_success.type],
  leaseCommission: [leaseCommissionEvents.commissionSaved.type, portfolioApiEvents.amendCommission_success.type],
  leaseInvoices: [
    leaseInvoicesEvents.leaseInvoiceTemplatesSaved.type,
    portfolioApiEvents.addInvoiceTemplate_success.type,
    portfolioApiEvents.updateInvoiceTemplate_success.type,
    portfolioApiEvents.removeInvoiceTemplate_success.type,
  ],
  leaseConfirmation: [leaseConfirmationEvents.approvalRequested.type, portfolioApiEvents.approve_success.type],
  userDetails: [userDetailsEvents.detailsSubmitted.type, userApiEvents.saveDetails_success.type],
  agencyDetails: [agencyDetailsEvents.detailsSubmitted.type],
}

// Build reverse mapping: action type -> dialog names[]
const actionTypeToDialogs: Record<string, string[]> = {}
Object.entries(dialogActionTypeMap).forEach(([dialogName, actionTypes]) => {
  actionTypes.forEach(actionType => {
    if (!actionTypeToDialogs[actionType]) {
      actionTypeToDialogs[actionType] = []
    }
    actionTypeToDialogs[actionType].push(dialogName)
  })
})

const dialogDiffSelectors = {
  propertyDetails: chatbotSelectors.getPropertyDetailsDialog,
  ownerDetails: chatbotSelectors.getOwnerDetailsDialog,
  tenantDetails: chatbotSelectors.getTenantDetailsDialog,
  leaseTerm: chatbotSelectors.getLeaseTermDialog,
  leaseRentAndFees: chatbotSelectors.getLeaseRentAndFeesDialog,
  leaseSettings: chatbotSelectors.getLeaseSettingsDialog,
  leaseCommission: chatbotSelectors.getLeaseCommissionDialog,
  leaseInvoices: chatbotSelectors.getLeaseInvoicesDialog,
  leaseConfirmation: chatbotSelectors.getLeaseConfirmationDialog,
  userDetails: chatbotSelectors.getCurrentUserDetailsDialog,
  agencyDetails: chatbotSelectors.getAgencyDetailsDialog,
}

const resolveDialogSelector = (dialog: any) => {
  if (dialogDiffSelectors[dialog] === undefined) {
    throw new Error('No selector assigned for dialog ' + dialog + ' in Chatbot/epics.js')
  }
  return dialogDiffSelectors[dialog]
}

export const dialogCaptureStateOnReducedEvent: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (
  action$,
  state$,
) =>
  action$.pipe(
    map(action => actionTypeToDialogs[action.type]),
    filter((dialogNames): dialogNames is string[] => dialogNames !== undefined && dialogNames.length > 0),
    mergeMap(dialogNames =>
      dialogNames.map(dialogName => {
        const dialogState = resolveDialogSelector(dialogName)(state$.value)
        const initialState = dialogInitialStates[dialogName]
        return { dialog: dialogName, initialState, dialogState }
      }),
    ),
    filter(({ initialState, dialogState }) => !isEqual(initialState, dialogState)),
    map(({ dialog, dialogState }) => chatbotEvents.captureDialogState({ dialog, dialogState })),
    tag('chatbot/epics/dialogCaptureStateOnReducedEvent'),
  )

export const createNewPortfolio: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.newPortfolioRequested),
    mergeMap(() => of(chatbotEvents.resetCurrentPortfolio(), uiEvents.redirect('/leases/new/edit'))),
    tag('chatbot/epics/createNewPortfolio'),
  )

export const requestPortfolioOnLoad: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.loadPortfolioRequested),
    pluck('payload'),
    map((id: string) => portfolioApiEvents.portfolio_request(id)),
    tag('chatbot/epics/requestPortfolioOnLoad'),
  )

export const setCurrentPortfolioOnLoadOnceFetched: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(chatbotEvents.loadPortfolioRequested),
    switchMap(() =>
      action$.pipe(
        ofType(portfolioApiEvents.portfolio_success),
        take(1),
        pluck('payload'),
        map(chatbotEvents.setCurrentPortfolio),
      ),
    ),
    tag('chatbot/epics/setCurrentPortfolioOnLoadOnceFetched'),
  )

export const confirmPropertyWithValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.propertyConfirmationRequested),
    pluck('payload'),
    mergeMap(
      (payload: {
        propertyDescription?: string
        buildingName?: string
        unitNumber?: string
        schemeName?: string
        schemeNumber?: string
      }) => [
        of(payload).pipe(
          filter(() => isPropertySelected(state$.value)),
          mergeMap(payload => {
            const state = state$.value
            const {
              propertyDescription = '',
              buildingName = '',
              unitNumber = '',
              schemeNumber = '',
              schemeName = '',
            } = payload
            const currentPortfolioId = chatbotSelectors.getCurrentPortfolioId(state)
            const id = getSelectedProperty(state)
            const lightstoneId = propertyApiSelectors.getPropertyLightstoneId(state)(id)
            const placeId = propertyApiSelectors.getPropertyPlaceId(state)(id)
            const leaseType = getCurrentLeaseType(state)
            const getCurrentAgencyGlobalVatEnabled = agencyApiSelectors.getCurrentAgencyGlobalVatEnabled(state)

            if (currentPortfolioId === null) {
              return [
                portfolioApiEvents.createPortfolio_request({
                  leaseType,
                  propertyId: id,
                  metaData: propertyDescription,
                }),
                chatbotEvents.setGlobalVat(getCurrentAgencyGlobalVatEnabled),
              ]
            }

            const existingUnitNumber = propertyApiSelectors.getUnitNumberById(state)(id)
            const existingBuildingName = propertyApiSelectors.getBuildingNameById(state)(id)

            // create new property if unit number or building name are changed
            if (buildingName !== existingBuildingName || unitNumber !== existingUnitNumber) {
              return [
                portfolioApiEvents.amendProperty_request({
                  body: {
                    property: {
                      placeId,
                      lightstoneId,
                      buildingName,
                      unitNumber,
                      schemeNumber,
                      schemeName,
                    },
                  },
                  params: { id: currentPortfolioId },
                }),
                portfolioApiEvents.amendMetaData_request({
                  body: { data: propertyDescription || '' },
                  params: { id: currentPortfolioId },
                }),
              ]
            }

            const { selectedProperty } = chatbotSelectors.getPropertyDetailsDialog(state$.value)

            return [
              propertyDetailsEvents.propertySelected({ id, propertyDescription, buildingName, unitNumber }),
              portfolioApiEvents.amendProperty_request({
                body: {
                  propertyId: selectedProperty,
                },
                params: { id: currentPortfolioId },
              }),
              portfolioApiEvents.amendMetaData_request({
                body: { data: propertyDescription || '' },
                params: { id: currentPortfolioId },
              }),
            ]
          }),
        ),
        of(payload).pipe(
          filter(() => isPlaceSelected(state$.value)),
          map(payload => {
            const placeId = getSelectedPlace(state$.value)
            const { propertyDescription, buildingName, unitNumber, schemeNumber, schemeName } = payload
            return { placeId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName }
          }),
          mergeMap(({ placeId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName }) => {
            const currentPortfolioId = chatbotSelectors.getCurrentPortfolioId(state$.value)
            const leaseType = getCurrentLeaseType(state$.value)
            const getCurrentAgencyGlobalVatEnabled = agencyApiSelectors.getCurrentAgencyGlobalVatEnabled(state$.value)

            if (currentPortfolioId === null) {
              return [
                portfolioApiEvents.createPortfolio_request({
                  leaseType,
                  metaData: propertyDescription,
                  property: {
                    placeId,
                    buildingName,
                    unitNumber,
                    schemeNumber,
                    schemeName,
                  },
                }),
                chatbotEvents.setGlobalVat(getCurrentAgencyGlobalVatEnabled),
              ]
            }

            return [
              portfolioApiEvents.amendProperty_request({
                body: {
                  property: {
                    placeId,
                    buildingName,
                    unitNumber,
                    schemeNumber,
                    schemeName,
                  },
                },
                params: { id: currentPortfolioId },
              }),
              portfolioApiEvents.amendMetaData_request({
                body: { data: propertyDescription || '' },
                params: { id: currentPortfolioId },
              }),
            ]
          }),
        ),
        of(payload).pipe(
          filter(() => isLightstonePropertySelected(state$.value)),
          map(payload => {
            const lightstoneId = getSelectedLightstoneProperty(state$.value)
            const { propertyDescription, buildingName, unitNumber, schemeNumber, schemeName } = payload
            return { lightstoneId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName }
          }),
          mergeMap(({ lightstoneId, propertyDescription, buildingName, unitNumber, schemeNumber, schemeName }) => {
            const currentPortfolioId = chatbotSelectors.getCurrentPortfolioId(state$.value)
            const leaseType = getCurrentLeaseType(state$.value)
            const getCurrentAgencyGlobalVatEnabled = agencyApiSelectors.getCurrentAgencyGlobalVatEnabled(state$.value)

            if (currentPortfolioId === null) {
              return [
                portfolioApiEvents.createPortfolio_request({
                  leaseType,
                  metaData: propertyDescription,
                  property: {
                    lightstoneId,
                    buildingName,
                    unitNumber,
                    schemeNumber,
                    schemeName,
                  },
                }),
                chatbotEvents.setGlobalVat(getCurrentAgencyGlobalVatEnabled),
              ]
            }

            return [
              portfolioApiEvents.amendProperty_request({
                body: {
                  property: {
                    lightstoneId,
                    buildingName,
                    unitNumber,
                    schemeNumber,
                    schemeName,
                  },
                },
                params: { id: currentPortfolioId },
              }),
              portfolioApiEvents.amendMetaData_request({
                body: { data: propertyDescription || '' },
                params: { id: currentPortfolioId },
              }),
            ]
          }),
        ),
      ],
    ),
    mergeAll(),
    tag('chatbot/epics/confirmPropertyWithValues'),
  )

export const handlePortfolioCreation: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(portfolioApiEvents.createPortfolio_success),
    pluck('payload'),
    mergeMap((payload: $TSFixMe) => {
      return [
        chatbotEvents.setCurrentPortfolio(payload),
        chatbotEvents.portfolioRedirect('/leases/:id/edit/property/owner'),
      ]
    }),
    tag('chatbot/epics/handlePortfolioCreation'),
  )

export const handlePropertyAmended: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(portfolioApiEvents.amendProperty_success),
    pluck('payload'),
    mergeMap((payload: $TSFixMe) => {
      // Only walk on to the next step while the lease is still a draft; on an active lease
      // the property section saves in place.
      const events: any[] = chatbotSelectors.isCurrentPortfolioDraft(state$.value)
        ? [chatbotEvents.portfolioRedirect('/leases/:id/edit/property/owner')]
        : []
      const propertyId = payload.propertyId
      const property = propertyApiSelectors.getPropertyById(state$.value)(propertyId)
      if (!property) {
        events.push(propertyApiEvents.property_request({ id: propertyId }))
      }
      return events
    }),
    tag('chatbot/epics/handlePropertyAmended'),
  )

export const submitTermValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.termValuesSubmitted),
    pluck('payload'),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return { ...values, ...{ id } }
    }),
    filter(({ id }) => id !== undefined && id !== null),
    map(values => leaseTermEvents.selectLeaseTerms(values)),
    tag('chatbot/epics/submitTermValues'),
  )

export const submitRentValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.rentValuesSubmitted),
    pluck('payload'),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return { ...values, ...{ id } }
    }),
    filter(({ id }) => id !== undefined && id !== null),
    map(values => leaseRentAndFeesEvents.rentAndFeesSaved(values)),
    tag('chatbot/epics/submitRentValues'),
  )

export const submitSettingsValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.settingsValuesSubmitted),
    pluck('payload'),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return { ...values, ...{ id } }
    }),
    filter(({ id }) => id !== undefined && id !== null),
    map(values => leaseSettingsEvents.settingsSaved(values)),
    tag('chatbot/epics/submitSettingsValues'),
  )

export const submitCommissionValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.commissionValuesSubmitted),
    pluck('payload'),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return { ...values, ...{ id } }
    }),
    filter(({ id }) => id !== undefined && id !== null),
    map(values => leaseCommissionEvents.commissionSaved(values)),
    tag('chatbot/epics/submitCommissionValues'),
  )

export const submitInvoiceValues: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.invoiceValuesSubmitted),
    pluck('payload'),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      return { ...values, ...{ id } }
    }),
    filter(({ id }) => id !== undefined && id !== null),
    map(values => leaseInvoicesEvents.leaseInvoiceTemplatesSaved(values)),
    tag('chatbot/epics/submitInvoiceValues'),
  )

export const approveCurrentPortfolio: Epic<AnyAction, AnyAction, RootState, EpicDependencies> = (action$, state$) =>
  action$.pipe(
    ofType(chatbotEvents.currentPortfolioActivationRequested),
    map(values => {
      const id = chatbotSelectors.getCurrentPortfolioId(state$.value)
      const body = {
        user: userApiSelectors.getUserId(state$.value),
        userEmail: userApiSelectors.getUserEmail(state$.value),
        notificationEmail: userApiSelectors.getUserEmail(state$.value),
      }
      const params = { id }
      return portfolioApiEvents.approve_request({ body, params })
    }),
    tag('chatbot/epics/approveCurrentPortfolio'),
  )
